import { apiClient, ApiError } from '@/lib/api-client';
import { authUtils } from '@/utils/auth';
import {
    FileUploadResponse,
    UpdateDocumentsRequest,
    UpdateDocumentsResponse,
} from '@/types/api';

// User-facing document type taxonomy (mirrors the backend ClientDocumentType
// enum). Used to classify and organize a client's documents.
// Phase E [PROJ-TAB-DATES-1]/[PROJ-TAB-RECV-1]: Permit Plans (the canonical
// name — never "Permit Set"), Permit Card, and Change Order joined the list.
// QUOTE is the Clients > Documents type for subcontractor quotes (UI: Quotes).
// RECEIVABLE is a client payment file — often a photo — labeled Receivable.
export const CLIENT_DOCUMENT_TYPES = [
    'CONTRACT',
    'PERMIT',
    'PERMIT_PLANS',
    'PERMIT_CARD',
    'HOA_APPROVAL',
    'INSPECTION',
    'CHANGE_ORDER',
    'INVOICE',
    'RECEIVABLE',
    'QUOTE',
    'DRAWING',
    'PHOTO',
    'OTHER',
] as const;
export type ClientDocumentType = (typeof CLIENT_DOCUMENT_TYPES)[number];

/**
 * Native file-picker accept list for Clients → Documents.
 * Matches estimate attachments / intake-style documents: PDF, Word, Excel,
 * text, and images (including HEIC). Do **not** use `image/*` here — that
 * hides PDFs and office files behind a photo-only library on phones.
 */
export const CLIENT_DOCUMENT_ACCEPT = [
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.txt',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.heic',
    '.heif',
    '.zip',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');

export interface FileUploadPayload {
    file: File;
    uploadType: string;
    organizationId: string;
    description?: string;
    tags?: string[];
    isPublic?: boolean;
}

export interface ClientFileUploadPayload {
    file: File;
    documentType: ClientDocumentType;
    category?: string;
    uploadType?: string;
    projectId?: string;
    description?: string;
    tags?: string[];
    contractOutcome?: 'won' | 'pending' | 'lost';
}

// Document response interface for existing documents
export interface ExistingDocument {
    id: string;
    fileName: string;
    originalFileName: string;
    fileSize: number;
    mimeType: string;
    fileUrl: string;
    category: string;
    uploadType: string;
    documentType?: ClientDocumentType;
    clientId?: string;
    isFinal?: boolean;
    markedFinalBy?: string;
    markedFinalAt?: string;
    description: string;
    tags: string[];
    isPublic: boolean;
    organizationId: string;
    uploadedBy?: string;
    createdAt: string;
    signedUrl: string;
}

// PUT a file to a presigned storage URL, reporting upload progress. Raw
// XMLHttpRequest because fetch cannot report upload progress. No auth header —
// the URL itself carries the short-lived authorization.
const putFileWithProgress = (
    url: string,
    file: File,
    onProgress?: (pct: number) => void,
): Promise<void> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        // Must match the Content-Type bound into the presigned signature.
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        };
        xhr.onload = () =>
            xhr.status >= 200 && xhr.status < 300
                ? resolve()
                : reject(new Error(`Storage upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Storage upload failed (network)'));
        xhr.send(file);
    });

export const fileUploadApi = {
    // Fetch existing documents for architect onboarding
    getArchitectOnboardingDocuments: async (organizationId: string): Promise<ExistingDocument[]> => {
        const url = `/uploads/architect-onboarding?organizationId=${encodeURIComponent(organizationId)}`;
        return apiClient.get<ExistingDocument[]>(url);
    },

    // Fetch existing documents for subcontractor onboarding
    getSubcontractorOnboardingDocuments: async (organizationId: string): Promise<ExistingDocument[]> => {
        const url = `/uploads/subcontractor-onboarding?organizationId=${encodeURIComponent(organizationId)}`;
        return apiClient.get<ExistingDocument[]>(url);
    },

    // Upload file for architect onboarding
    uploadArchitectFile: async (payload: FileUploadPayload): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', payload.file);
        formData.append('uploadType', payload.uploadType);
        formData.append('organizationId', payload.organizationId);

        if (payload.description) {
            formData.append('description', payload.description);
        }

        if (payload.tags && payload.tags.length > 0) {
            payload.tags.forEach(tag => {
                formData.append('tags', tag);
            });
        }

        if (payload.isPublic !== undefined) {
            formData.append('isPublic', payload.isPublic.toString());
        }

        return apiClient.post<FileUploadResponse>('/uploads/architect-onboarding', formData);
    },    // Upload file for subcontractor onboarding
    uploadSubcontractorFile: async (payload: FileUploadPayload): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', payload.file);
        formData.append('uploadType', payload.uploadType);
        formData.append('organizationId', payload.organizationId);

        if (payload.description) {
            formData.append('description', payload.description);
        }

        if (payload.tags && payload.tags.length > 0) {
            payload.tags.forEach(tag => {
                formData.append('tags', tag);
            });
        }

        if (payload.isPublic !== undefined) {
            formData.append('isPublic', payload.isPublic.toString());
        }

        return apiClient.post<FileUploadResponse>('/uploads/subcontractor-onboarding', formData);
    },

    // Update architect documents
    updateArchitectDocuments: async (architectId: string, data: UpdateDocumentsRequest): Promise<UpdateDocumentsResponse> => {
        return apiClient.put<UpdateDocumentsResponse>(`/architects/${architectId}/documents`, data);
    },

    // Update subcontractor documents  
    updateSubcontractorDocuments: async (subcontractorId: string, data: UpdateDocumentsRequest): Promise<UpdateDocumentsResponse> => {
        return apiClient.put<UpdateDocumentsResponse>(`/subcontractors/${subcontractorId}/documents`, data);
    },

    // Delete uploaded file by ID
    deleteFile: async (fileId: string): Promise<void> => {
        return apiClient.delete<void>(`/uploads/${fileId}`);
    },

    // Upload general file (for project notes, etc.)
    uploadFile: async (file: File, category: string, uploadType: string, organizationId: string): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        formData.append('uploadType', uploadType);
        formData.append('organizationId', organizationId);

        return apiClient.post<FileUploadResponse>('/uploads', formData);
    },

    // Sprint 41 ERS-8 — project-scoped upload with explicit category.
    // Posts to /uploads/project/:projectId; the backend whitelists
    // PROJECT_PICTURES + PROJECT_SKETCH (anything else falls back to
    // PROJECT_UPLOAD). Used by the dedicated upload zones on the
    // intake form's summary page; the resulting FileUpload rows drive
    // the 3 status markers on the Estimator Review Queue.
    uploadProjectFile: async (
        projectId: string,
        file: File,
        category: string,
        uploadType: string,
        description?: string,
        tags?: string[],
        documentType?: ClientDocumentType,
        opts?: {
            /** User-facing file name set at upload (Job Log composer rename). */
            displayName?: string;
            /** Job-log entry to link — the photo then surfaces as Progress. */
            dailyLogId?: string;
            /** Also file the upload on this client (paper-signed contracts). */
            clientId?: string;
            /** Won / pending / lost when the file is a contract. */
            contractOutcome?: 'won' | 'pending' | 'lost';
        },
    ): Promise<FileUploadResponse> => {
        const organizationId = authUtils.getOrganizationId();
        if (!organizationId) {
            throw new Error('Organization ID not found. Please ensure you are logged in.');
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        formData.append('uploadType', uploadType);
        formData.append('organizationId', organizationId);
        if (description) formData.append('description', description);
        // Phase E: every Documents upload selects a type ([PROJ-TAB-DOCS-1]).
        if (documentType) formData.append('documentType', documentType);
        // Job Log ([PROJ-TAB-JOBLOG-1]): name + entry link set at upload.
        if (opts?.displayName) formData.append('displayName', opts.displayName);
        if (opts?.dailyLogId) formData.append('dailyLogId', opts.dailyLogId);
        if (opts?.clientId) formData.append('clientId', opts.clientId);
        if (opts?.contractOutcome) formData.append('contractOutcome', opts.contractOutcome);
        // Repeated fields, matching uploadClientFile — the backend's
        // normalizeTags accepts an array, NOT a JSON-encoded string.
        tags?.forEach((tag) => formData.append('tags', tag));

        return apiClient.post<FileUploadResponse>(
            `/uploads/project/${encodeURIComponent(projectId)}`,
            formData,
        );
    },

    /**
     * Replace the tag list on an existing upload (production before-photo
     * area repair). Send the FULL list — auto tags included.
     */
    updateTags: async (uploadId: string, tags: string[]): Promise<FileUploadResponse> =>
        apiClient.patch<FileUploadResponse>(`/uploads/${encodeURIComponent(uploadId)}/tags`, {
            tags,
        }),

    // Upload file for a specific partner
    uploadPartnerFile: async (
        file: File,
        partnerId: string,
        category: string,
        uploadType: string,
        _organizationId: string,
        description?: string,
    ): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        formData.append('uploadType', uploadType);
        if (description) {
            formData.append('description', description);
        }

        return apiClient.post<FileUploadResponse>(`/uploads/partner/${partnerId}`, formData);
    },

    // Get all documents for a specific partner
    getPartnerDocuments: async (partnerId: string): Promise<ExistingDocument[]> => {
        return apiClient.get<ExistingDocument[]>(`/uploads/partner/${partnerId}`);
    },

    // Direct-to-storage client upload: presign → the browser PUTs the file
    // straight to storage (reporting progress) → register the object. Cuts
    // the transfer in half for large files versus proxying through the API.
    // Falls back to the classic proxied upload when presigning is
    // unavailable (local storage provider, older API) or the direct PUT
    // fails (e.g. storage CORS not yet configured) — validation failures
    // (400) surface instead of falling back, since the classic path would
    // reject them identically.
    uploadClientFileSmart: async (
        clientId: string,
        payload: ClientFileUploadPayload,
        onProgress?: (pct: number) => void,
    ): Promise<FileUploadResponse | ExistingDocument> => {
        try {
            const ticket = await apiClient.post<{ uploadUrl: string; fileUrl: string; expiresIn: number }>(
                `/uploads/client/${encodeURIComponent(clientId)}/direct`,
                {
                    fileName: payload.file.name,
                    mimeType: payload.file.type || 'application/octet-stream',
                    fileSize: payload.file.size,
                    documentType: payload.documentType,
                },
            );
            await putFileWithProgress(ticket.uploadUrl, payload.file, onProgress);
            return await apiClient.post<ExistingDocument>(
                `/uploads/client/${encodeURIComponent(clientId)}/direct/complete`,
                {
                    fileUrl: ticket.fileUrl,
                    fileName: payload.file.name,
                    documentType: payload.documentType,
                    description: payload.description,
                    tags: payload.tags,
                    contractOutcome: payload.contractOutcome,
                },
            );
        } catch (err) {
            if (err instanceof ApiError && err.status === 400) throw err;
            console.warn('Direct upload unavailable — falling back to classic upload:', err);
            return fileUploadApi.uploadClientFile(clientId, payload);
        }
    },

    // Upload a confidential document for a specific client. The file is scoped
    // to the authenticated user's organization (from the token) and the given
    // client (from the route); a user-facing documentType is required.
    uploadClientFile: async (
        clientId: string,
        payload: ClientFileUploadPayload,
    ): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', payload.file);
        formData.append('category', payload.category ?? 'GENERAL');
        formData.append('uploadType', payload.uploadType ?? 'OTHER');
        formData.append('documentType', payload.documentType);
        if (payload.projectId) formData.append('projectId', payload.projectId);
        if (payload.contractOutcome) formData.append('contractOutcome', payload.contractOutcome);
        if (payload.description) formData.append('description', payload.description);
        if (payload.tags && payload.tags.length > 0) {
            payload.tags.forEach(tag => formData.append('tags', tag));
        }

        return apiClient.post<FileUploadResponse>(
            `/uploads/client/${encodeURIComponent(clientId)}`,
            formData,
        );
    },

    // List a client's documents, optionally filtered by document type. Scoped
    // server-side to the caller's organization and the given client.
    getClientDocuments: async (
        clientId: string,
        documentType?: ClientDocumentType,
    ): Promise<ExistingDocument[]> => {
        const query = documentType
            ? `?documentType=${encodeURIComponent(documentType)}`
            : '';
        return apiClient.get<ExistingDocument[]>(
            `/uploads/client/${encodeURIComponent(clientId)}${query}`,
        );
    },

    // Mark or unmark a file as a final copy (multiple finals allowed).
    markFinal: async (fileId: string, isFinal: boolean): Promise<ExistingDocument> => {
        return apiClient.patch<ExistingDocument>(
            `/uploads/${encodeURIComponent(fileId)}/final`,
            { isFinal },
        );
    },

    // Replace the auto-assigned display name of an upload with a custom name.
    renameFile: async (
        fileId: string,
        fileName: string,
    ): Promise<ExistingDocument> => {
        return apiClient.patch<ExistingDocument>(
            `/uploads/${encodeURIComponent(fileId)}/name`,
            { fileName },
        );
    },

    // Edit the user-facing document type of an existing upload.
    updateDocumentType: async (
        fileId: string,
        documentType: ClientDocumentType,
    ): Promise<ExistingDocument> => {
        return apiClient.patch<ExistingDocument>(
            `/uploads/${encodeURIComponent(fileId)}/document-type`,
            { documentType },
        );
    },

    attachToProject: async (
        fileId: string,
        projectId: string,
    ): Promise<ExistingDocument> => {
        return apiClient.patch<ExistingDocument>(
            `/uploads/${encodeURIComponent(fileId)}/project`,
            { projectId },
        );
    },
};
